import React, { useState, useCallback } from "react";
import Cropper from "react-easy-crop";

function createImage(src) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", (error) => reject(error));
    image.setAttribute("crossOrigin", "anonymous");
    image.src = src;
  });
}

async function getCroppedCircularImage(imageSrc, cropPixels) {
  const image = await createImage(imageSrc);
  const cropSize = Math.max(cropPixels.width, cropPixels.height);
  
  // Limit output size to 512x512px for better performance and smaller file size
  // (avatars are displayed at max 100px, so 512px is plenty for retina displays)
  const MAX_AVATAR_SIZE = 512;
  const outputSize = Math.min(cropSize, MAX_AVATAR_SIZE);
  
  const canvas = document.createElement("canvas");
  canvas.width = outputSize;
  canvas.height = outputSize;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("Canvas not supported");
  }

  ctx.fillStyle = "transparent";
  ctx.fillRect(0, 0, outputSize, outputSize);

  ctx.save();
  ctx.beginPath();
  ctx.arc(outputSize / 2, outputSize / 2, outputSize / 2, 0, Math.PI * 2);
  ctx.closePath();
  ctx.clip();

  ctx.drawImage(
    image,
    cropPixels.x,
    cropPixels.y,
    cropPixels.width,
    cropPixels.height,
    0,
    0,
    outputSize,
    outputSize
  );

  ctx.restore();

  return new Promise((resolve, reject) => {
    // Use PNG to preserve transparency for circular avatars
    // Quality 0.85 provides good balance between quality and file size
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("Failed to create image blob"));
          return;
        }
        resolve(blob);
      },
      "image/png",
      0.85
    );
  });
}

export default function AvatarCropModal({
  imageSrc,
  onCancel,
  onConfirm,
  labels = {},
}) {
  const {
    zoomLabel = "Zoom",
    cancelLabel = "Cancel",
    confirmLabel = "Save",
    processingLabel = "Saving...",
    selectAreaMessage = "Please select an area first.",
    errorMessageText = "Could not process the image. Please try another photo.",
  } = labels;
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const handleCropComplete = useCallback((_, areaPixels) => {
    setCroppedAreaPixels(areaPixels);
  }, []);

  const handleConfirm = useCallback(async () => {
    if (!croppedAreaPixels) {
      setErrorMessage(selectAreaMessage);
      return;
    }
    setIsProcessing(true);
    setErrorMessage("");
    try {
      const blob = await getCroppedCircularImage(imageSrc, {
        x: Math.round(croppedAreaPixels.x),
        y: Math.round(croppedAreaPixels.y),
        width: Math.round(croppedAreaPixels.width),
        height: Math.round(croppedAreaPixels.height),
      });
      onConfirm(blob);
    } catch (error) {
      console.error("Failed to crop avatar", error);
      setErrorMessage(errorMessageText);
    } finally {
      setIsProcessing(false);
    }
  }, [croppedAreaPixels, imageSrc, onConfirm, errorMessageText, selectAreaMessage]);

  return (
    <>
      <div className="avatar-crop-overlay" onClick={(e) => {
        if (e.target === e.currentTarget) {
          e.stopPropagation();
          onCancel();
        }
      }} />
      <div className="avatar-crop-modal" role="dialog" aria-modal="true">
        <div className="avatar-cropper">
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={1}
            cropShape="round"
            showGrid={false}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={handleCropComplete}
          />
        </div>
        <div className="avatar-crop-controls">
          <label className="avatar-crop-slider-label" htmlFor="avatar-zoom-input">
            {zoomLabel}
          </label>
          <input
            id="avatar-zoom-input"
            type="range"
            min="1"
            max="3"
            step="0.01"
            value={zoom}
            onChange={(event) => setZoom(Number(event.target.value))}
          />
        </div>
        {errorMessage && <div className="avatar-crop-error">{errorMessage}</div>}
        <div className="avatar-crop-actions">
          <button
            type="button"
            className="avatar-crop-cancel"
            onClick={onCancel}
            disabled={isProcessing}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            className="avatar-crop-confirm"
            onClick={handleConfirm}
            disabled={isProcessing}
          >
            {isProcessing ? processingLabel : confirmLabel}
          </button>
        </div>
      </div>
    </>
  );
}

